import { processStreamedResponse } from '#lib/process-streamed-response.ts';
import { handleApiError } from '#lib/util/error-handling/handle-api-error.ts';
import { Fetchable } from '#lib/util/fetchable';
import { globalState } from '#lib/util/global-state.svelte.ts';
import { nid } from '#lib/util/nanoid';
import { DEFAULT_FALLBACK_MODEL } from '@astlai/common/util/models';
import { AntonioChatApiService } from '#src/services/antonio-chat-api.service.ts';
import {
	ANTONIO_START_STEP,
	type AntonioInput,
	type AntonioStepOption,
	type AntonioStepView
} from '#src/types/antonio-step.ts';
import { ChatMessage, type AssetRef } from '#src/types/chat.ts';
import { getContext, setContext } from 'svelte';

/**
 * A chat message, or the visitor's pick in a choice step: the picked options,
 * shown as a pick rather than a bubble, since the visitor never wrote it.
 */
export type AntonioChatMessage = ChatMessage & { pick?: AntonioStepOption[] };

interface AntonioTurnRequest {
	stepKey: string;
	input: AntonioInput;
	/** The visitor's entry in the chat, shown right away. */
	message?: AntonioChatMessage;
}

/**
 * Deliberately much smaller than ChatContext — antonio conversations have no
 * citations, sources, retrieval context, or knowledge base, so none of that
 * machinery is reused here. Talks to /api/antonio-chat instead of /api/chat.
 *
 * Knows nothing about the funnel: every visitor input goes to the same
 * endpoint, answering the step the server last named, and the server decides
 * what happens next and which step comes after (see backend/routes/antonio_funnel.go).
 */
export class AntonioChatContext {
	readonly fetchable = new Fetchable();

	conversationId: string | undefined = $state();
	messages: AntonioChatMessage[] = $state([]);
	/** Set once start has been sent, which dismisses the onboarding screen and keeps a re-running effect from sending it twice. */
	started = $state(false);
	/** The step the footer renders, as the server last named it (antonio.step). Starts in the one step every client knows. */
	step: AntonioStepView = $state(ANTONIO_START_STEP);
	/** The uploaded photo, shown on the visitor's next message and then cleared. Display only: the photo itself is stored on the conversation. */
	pendingAssets: AssetRef[] = $state([]);

	/** The last request, resent by retry when it failed before a conversation existed to retry. */
	private lastRequest: AntonioTurnRequest | undefined = $state.raw();

	private currentAbortController: AbortController | null = $state(null);
	readonly isStreaming = $derived(this.currentAbortController !== null);

	/** The visitor confirmed generating and is waiting for the image. */
	readonly generatingImage = $derived(
		this.isStreaming &&
			this.lastRequest?.input.type === 'confirm_generate' &&
			this.lastRequest.input.confirmed
	);

	readonly presentableMessages = $derived(
		this.messages.filter(
			(m) =>
				(m.role !== 'system' && !!m.content) ||
				!!m.pick?.length ||
				!!m.reasoning_content ||
				(m.role === 'assistant' && !!m.toolCalls?.length)
		)
	);

	readonly latestAssistantMessage = $derived(
		this.messages.toReversed().find((m) => m.role === 'assistant')
	);

	private readonly apiService: AntonioChatApiService;
	private readonly modelOverride?: string;

	constructor(baseUrl?: string, apiKey?: string, modelOverride?: string) {
		this.apiService = new AntonioChatApiService(baseUrl, apiKey);
		this.modelOverride = modelOverride;
	}

	private get instanceId() {
		return globalState.instance?.id ?? '';
	}

	private get model() {
		return this.modelOverride ?? globalState.instance?.default_model ?? DEFAULT_FALLBACK_MODEL;
	}

	/** Seeds the instance's welcome message as the first assistant bubble, without kicking off the antonio flow. */
	seedWelcomeMessage() {
		const welcomeMessage = globalState.instance?.welcome_message;
		if (welcomeMessage) {
			const message = new ChatMessage('assistant', welcomeMessage);
			message.status = 'completed';
			this.upsertMessage(message);
		}
	}

	/**
	 * Opens the funnel: dismisses the onboarding screen, seeds the welcome
	 * message, and asks the server for the first step. The server answers with
	 * the LLM's opening (with an onboarding screen) or just the chat input
	 * (without one, the visitor types first), so the client needs to know none
	 * of it.
	 */
	async start() {
		if (this.started || this.step.kind !== 'start') {
			return;
		}
		this.started = true;
		if (this.messages.length === 0) {
			this.seedWelcomeMessage();
		}
		await this.send({ type: 'start' });
	}

	/**
	 * Sends one input for the active step. displayText becomes the visitor's
	 * bubble right away and travels along as the message the server stores as
	 * their turn. A pick has none: it shows as the picked options instead, and
	 * the server writes that turn itself. Only start shows nothing. Resolves
	 * to whether it went through.
	 */
	async send(input: AntonioInput, displayText?: string): Promise<boolean> {
		await this.fetchable.fetch(async () => {
			let message: AntonioChatMessage | undefined;
			if (input.type === 'choice' && this.step.kind === 'choice') {
				const { optionIds } = input;
				message = new ChatMessage('user', '');
				message.pick = this.step.options.filter((option) => optionIds.includes(option.id));
				this.upsertMessage(message);
			} else if (displayText !== undefined) {
				message = new ChatMessage('user', displayText);
				if (this.pendingAssets.length) {
					message.assets = this.pendingAssets;
					this.pendingAssets = [];
				}
				this.upsertMessage(message);
			}
			await this.request({ stepKey: this.step.key, input, message });
		});
		return !this.fetchable.error;
	}

	async chat(userMessage: string) {
		await this.send({ type: 'text' }, userMessage);
	}

	upsertMessage(message: AntonioChatMessage, tempId?: string) {
		const targetId = message.id ?? tempId;
		const i = this.messages.findIndex((m) => m.id === targetId);
		if (i > -1) {
			this.messages[i] = { ...this.messages[i], ...message };
			return;
		}
		this.messages = [...this.messages, message];
	}

	private removeMessage(id: string | undefined) {
		this.messages = this.messages.filter((m) => m.id !== id);
	}

	private async request(turn: AntonioTurnRequest, retry = false) {
		this.lastRequest = turn;

		const tempId = nid();
		let accMessage: ChatMessage = {
			created: new Date(),
			id: tempId,
			status: 'pending',
			content: '',
			reasoning_content: '',
			role: 'assistant',
			type: 'message'
		};
		this.upsertMessage(accMessage);

		const abortController = new AbortController();
		this.currentAbortController = abortController;

		try {
			const response =
				retry && this.conversationId
					? await this.apiService.retryChat(this.conversationId, abortController.signal)
					: await this.apiService.chat(
							{
								conversationId: this.conversationId,
								stepKey: turn.stepKey,
								input: turn.input,
								// A pick is only shown here: the server writes it itself.
								message: turn.message?.pick ? undefined : turn.message,
								model: this.model,
								instance: this.instanceId
							},
							abortController.signal
						);

			// The step had moved on (a double click, a stale tab): take the
			// server's step and drop the input instead of showing an error.
			if (response.status === 409) {
				const body: { step?: AntonioStepView } = await response.json();
				if (body.step) {
					this.step = body.step;
				}
				this.removeMessage(tempId);
				this.removeMessage(turn.message?.id);
				return;
			}

			await handleApiError(response);

			const stream = processStreamedResponse(response, accMessage, abortController);

			for await (const { eventType, eventData, message } of stream) {
				if (eventType === 'conversation.created') {
					this.conversationId = eventData.id as string;
				} else if (eventType === 'antonio.step') {
					this.step = eventData.step as AntonioStepView;
				}
				accMessage = message;
				this.upsertMessage(accMessage, tempId);
			}
		} catch (e) {
			const isAbortError = e instanceof Error && e.name === 'AbortError';
			if (!isAbortError) {
				this.removeMessage(tempId);
				// An input whose side effect went through before the LLM was
				// refused (the usage limit) still moved the funnel on.
				const step = (e as Error)?.cause as { step?: AntonioStepView } | undefined;
				if (step?.step) {
					this.step = step.step;
				}
				throw e;
			}
		} finally {
			this.currentAbortController = null;
			// A turn the server answered without a message of its own (e.g.
			// start, when the visitor types first) leaves nothing to show.
			const answered =
				!!accMessage.content || !!accMessage.reasoning_content || !!accMessage.toolCalls?.length;
			if (!answered) {
				this.removeMessage(accMessage.id ?? tempId);
			}
		}
	}

	/**
	 * Uploads the visitor's own photo onto the conversation, where
	 * generate_image takes it from (see backend/routes/antonio_user_image.go).
	 * Uploading again replaces it. The conversation always exists by then: the
	 * image step only comes after the interview.
	 *
	 * The image goes to the image model on every generate_image call. It is also
	 * shown to the LLM itself, but only on the one turn it writes the generation
	 * prompt on, and only when the instance's model can see images at all (see
	 * rag/app/reference_image.py's reference_image_for_vision).
	 */
	async uploadImage(file: File) {
		if (!this.conversationId) {
			return;
		}
		const { url } = await this.apiService.uploadUserImage(this.conversationId, file);
		this.pendingAssets = [{ kind: 'image', source: 'user', status: 'ready', name: file.name, url }];
	}

	async retry() {
		this.fetchable.fetch(async () => {
			this.fetchable.error = null;
			// No conversation yet: nothing to regenerate server-side, so resend
			// the request itself.
			if (!this.conversationId) {
				if (this.lastRequest) {
					await this.request(this.lastRequest);
				}
				return;
			}
			if (this.messages.at(-1)?.role === 'assistant') {
				this.messages = this.messages.slice(0, -1);
			}
			// The server drops its own trailing assistant message, if any, and
			// regenerates from the stored transcript — antonio_conversation has
			// no client-writable update rule for this client to persist the
			// truncation itself (unlike ChatContext's retry, which does via
			// updateConversation before resending).
			await this.request(
				this.lastRequest ?? { stepKey: this.step.key, input: { type: 'text' } },
				true
			);
		});
	}

	reset() {
		this.conversationId = undefined;
		this.messages = [];
		this.started = false;
		this.step = ANTONIO_START_STEP;
		this.pendingAssets = [];
		this.lastRequest = undefined;
	}
}

export const ANTONIO_CHAT_CONTEXT_KEY = Symbol('antonio-chat');

export function getAntonioChatContext() {
	return getContext<AntonioChatContext>(ANTONIO_CHAT_CONTEXT_KEY);
}

export function setAntonioChatContext(ctx: AntonioChatContext) {
	setContext(ANTONIO_CHAT_CONTEXT_KEY, ctx);
	return ctx;
}
